//src/hooks/useRisks.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createRisk,
  deleteRisk,
  getRisk,
  linkActionPlanModelToRisk,
  listAvailableActionPlanModels,
  listRiskActionPlanModels,
  listRiskCategories,
  listRisks,
  setRiskStatus,
  unlinkActionPlanModelFromRisk,
  updateRisk,
} from "@/services/risks";
import type { GroCrudScope } from "@/lib/groCrudScope";
import type {
  RiskActionPlanModelLinkIn,
  RiskCatalogCreateIn,
  RiskCatalogUpdateIn,
  RiskTheme,
} from "@/types/risks";

export const RISK_CATEGORIES_QK = (
  params: { risk_theme?: RiskTheme | ""; is_active?: boolean },
  scope: GroCrudScope = "client",
) => ["risk_categories", scope, params] as const;

export const RISKS_QK = (
  params: { q?: string; risk_category_id?: string; risk_theme?: RiskTheme | ""; is_active?: boolean },
  scope: GroCrudScope = "client",
) => ["risks", scope, params] as const;

export const RISK_DETAIL_QK = (riskId: string | undefined, scope: GroCrudScope = "client") =>
  ["risk_detail", scope, riskId] as const;

export const RISK_ACTION_PLAN_MODELS_QK = (
  riskId: string | undefined,
  scope: GroCrudScope = "client",
) => ["risk_action_plan_models", scope, riskId] as const;

export const AVAILABLE_ACTION_PLAN_MODELS_QK = (
  params: { q?: string; is_active?: boolean },
  scope: GroCrudScope = "client",
) => ["available_action_plan_models", scope, params] as const;

export function useRiskCategories(
  params: { risk_theme?: RiskTheme | ""; is_active?: boolean },
  scope: GroCrudScope = "client",
) {
  return useQuery({
    queryKey: RISK_CATEGORIES_QK(params, scope),
    queryFn: () => listRiskCategories(params, scope),
    staleTime: 60_000,
  });
}

export function useRisks(
  params: { q?: string; risk_category_id?: string; risk_theme?: RiskTheme | ""; is_active?: boolean },
  scope: GroCrudScope = "client",
) {
  return useQuery({
    queryKey: RISKS_QK(params, scope),
    queryFn: () => listRisks(params, scope),
    staleTime: 20_000,
  });
}

export function useRiskDetail(riskId: string | undefined, scope: GroCrudScope = "client") {
  return useQuery({
    queryKey: RISK_DETAIL_QK(riskId, scope),
    queryFn: () => getRisk(riskId as string, scope),
    enabled: !!riskId,
    staleTime: 20_000,
  });
}

export function useRiskActionPlanModels(riskId: string | undefined, scope: GroCrudScope = "client") {
  return useQuery({
    queryKey: RISK_ACTION_PLAN_MODELS_QK(riskId, scope),
    queryFn: () => listRiskActionPlanModels(riskId as string, scope),
    enabled: !!riskId,
    staleTime: 20_000,
  });
}

export function useAvailableActionPlanModels(
  riskId: string | undefined,
  params: { q?: string; is_active?: boolean },
  scope: GroCrudScope = "client",
) {
  return useQuery({
    queryKey: AVAILABLE_ACTION_PLAN_MODELS_QK(params, scope),
    queryFn: () => listAvailableActionPlanModels(params, scope),
    enabled: !!riskId,
    staleTime: 20_000,
  });
}

export function useCreateRisk(scope: GroCrudScope = "client") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RiskCatalogCreateIn) => createRisk(body, scope),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["risks", scope] }),
  });
}

export function useUpdateRisk(scope: GroCrudScope = "client") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ riskId, body }: { riskId: string; body: RiskCatalogUpdateIn }) =>
      updateRisk(riskId, body, scope),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["risks", scope] });
      qc.invalidateQueries({ queryKey: RISK_DETAIL_QK(variables.riskId, scope) });
    },
  });
}

export function useSetRiskStatus(scope: GroCrudScope = "client") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ riskId, is_active }: { riskId: string; is_active: boolean }) =>
      setRiskStatus(riskId, { is_active }, scope),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["risks", scope] });
      qc.invalidateQueries({ queryKey: RISK_DETAIL_QK(variables.riskId, scope) });
    },
  });
}

export function useDeleteRisk(scope: GroCrudScope = "client") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (riskId: string) => deleteRisk(riskId, scope),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["risks", scope] }),
  });
}

export function useLinkActionPlanModelToRisk(scope: GroCrudScope = "client") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ riskId, body }: { riskId: string; body: RiskActionPlanModelLinkIn }) =>
      linkActionPlanModelToRisk(riskId, body, scope),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: RISK_DETAIL_QK(variables.riskId, scope) });
      qc.invalidateQueries({ queryKey: RISK_ACTION_PLAN_MODELS_QK(variables.riskId, scope) });
      qc.invalidateQueries({ queryKey: ["available_action_plan_models", scope] });
      qc.invalidateQueries({ queryKey: ["company_gro_templates"] });
    },
  });
}

export function useUnlinkActionPlanModelFromRisk(scope: GroCrudScope = "client") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ riskId, actionPlanModelId }: { riskId: string; actionPlanModelId: string }) =>
      unlinkActionPlanModelFromRisk(riskId, actionPlanModelId, scope),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: RISK_DETAIL_QK(variables.riskId, scope) });
      qc.invalidateQueries({ queryKey: RISK_ACTION_PLAN_MODELS_QK(variables.riskId, scope) });
      qc.invalidateQueries({ queryKey: ["available_action_plan_models", scope] });
      qc.invalidateQueries({ queryKey: ["company_gro_templates"] });
    },
  });
}
